import { createStore, applyMiddleware, combineReducers } from 'redux';
import thunk from 'redux-thunk';
import { composeWithDevTools } from 'redux-devtools-extension';
import axios from 'axios';
import { loginReducer, registerReducer } from './reducers/authReducers';
import { usersReducer } from './reducers/usersReducers';
import { questionsReducer, commentsReducer } from './reducers/gameReducers';
import { socialReducer } from './reducers/socialReducers';
import { LOGIN_SUCCESS, LOGOUT } from './types/auth';
import { DOMAIN } from './constants';

const reducer = combineReducers({
  userLogin: loginReducer,
  userRegister: registerReducer,

  listUser: usersReducer,
  questionsFeed: questionsReducer,
  questionComments: commentsReducer,
  social: socialReducer,
});

const userInfoFromStorage = (() => {
  try {
    const raw = localStorage.getItem('userInfo');
    return raw ? JSON.parse(raw) : null;
  } catch {
    localStorage.removeItem('userInfo');
    return null;
  }
})();

const initialState = {
  userLogin: { userInfo: userInfoFromStorage },
};
const middleware = [thunk];

const store = createStore(reducer, initialState, composeWithDevTools({ maxAge: 200 })(applyMiddleware(...middleware)));

// Axios interceptor: auto-refresh expired access tokens
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    // Don't intercept login/register/refresh/anon requests
    const url = originalRequest.url || '';
    if (
      url.includes('/login') ||
      url.includes('/register') ||
      url.includes('/refresh_token') ||
      url.includes('/anon')
    ) {
      return Promise.reject(error);
    }

    let storedUser = null;
    try {
      storedUser = JSON.parse(localStorage.getItem('userInfo') || 'null');
    } catch {
      storedUser = null;
    }
    if (!storedUser?.access) {
      return Promise.reject(error);
    }

    const userInfo = store.getState().userLogin?.userInfo || storedUser;
    if (!userInfo?.refresh) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then((token) => {
        // Mark queued replays as retried too. Without this, a queued
        // request that 401s AGAIN after the shared refresh (revoked
        // session, endpoint using 401 for authorization) re-enters this
        // interceptor with _retry unset and kicks off a fresh refresh
        // cycle - an unbounded refresh/retry loop.
        originalRequest._retry = true;
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return axios(originalRequest);
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const { data } = await axios.post(`${DOMAIN}/api/v1/accounts/refresh_token/`, { refresh: userInfo.refresh });
      const updatedInfo = { ...userInfo, access: data.access, refresh: data.refresh };
      localStorage.setItem('userInfo', JSON.stringify(updatedInfo));
      store.dispatch({ type: LOGIN_SUCCESS, payload: updatedInfo });
      originalRequest.headers.Authorization = `Bearer ${data.access}`;
      processQueue(null, data.access);
      return axios(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError, null);
      localStorage.removeItem('userInfo');
      store.dispatch({ type: LOGOUT });
      // Send the user to login instead of leaving them stranded on an
      // authenticated screen where every subsequent request 401s and the
      // page just silently stops working. Guard against redirect loops if
      // a 401 somehow fires while already on an auth page.
      // Only claimed users get bounced to /login. Anonymous identities are
      // minted automatically (loadOrCreateAnon) and a failed refresh just
      // means "mint a new one" - redirecting them destroys an anon session
      // mid-browse and dead-ends users who have no account to log into.
      const path = window.location.pathname;
      // Read isAnon from the STORED identity (localStorage), not the Redux
      // copy: the store can hold a stale pre-mint value while localStorage
      // always reflects the identity whose refresh just failed.
      const identity = storedUser || userInfo;
      const wasClaimed = !!identity && identity.isAnon !== true;
      if (wasClaimed && !path.startsWith('/login') && !path.startsWith('/register')) {
        window.location.assign('/login');
      }
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

export default store;
